import { createContext, useContext } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import type { Auth } from "firebase/auth";
import { wrapCompatFirestore } from "../utility/firestoreGateway";
import {
  API_KEY,
  AUTH_DOMAIN,
  PROJECT_ID,
  STORAGE_BUCKET,
  MESSAGIN_SENDER_ID,
  APP_ID,
  MEASUREMENT_ID
} from "../secretKeys";

const useFirebaseEmulators =
  process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true";
const emulatorProjectId =
  process.env.REACT_APP_FIREBASE_PROJECT_ID || "demo-advanced-movie-rating";

if (firebase.apps.length === 0) {
  firebase.initializeApp(useFirebaseEmulators ? {
    apiKey: "demo-api-key",
    authDomain: `${emulatorProjectId}.firebaseapp.com`,
    projectId: emulatorProjectId,
    storageBucket: `${emulatorProjectId}.appspot.com`,
    messagingSenderId: "000000000000",
    appId: "1:000000000000:web:firestore-security-smoke"
  } : {
    apiKey: API_KEY,
    authDomain: AUTH_DOMAIN,
    projectId: PROJECT_ID,
    storageBucket: STORAGE_BUCKET,
    messagingSenderId: MESSAGIN_SENDER_ID,
    appId: APP_ID,
    measurementId: MEASUREMENT_ID
  });
}

const clearFirestorePersistenceLocalStorage = () => {
  if (typeof window === "undefined") return;
  let localStorageRef: Storage | null = null;
  try {
    localStorageRef = window.localStorage;
  } catch {
    return;
  }
  if (!localStorageRef) return;

  const prefixes = [
    "firestore_targets_",
    "firestore_sequence_number_",
    "firestore_mutations_",
    "firestore_bundle_loaded_"
  ];

  try {
    for (let index = localStorageRef.length - 1; index >= 0; index -= 1) {
      const key = localStorageRef.key(index);
      if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
        localStorageRef.removeItem(key);
      }
    }
  } catch (error) {
    console.warn("Не удалось очистить локальный кеш Firestore:", error);
  }
};

// Firestore offline persistence stores query target metadata in browser
// Storage. On large profiles that can exceed quota and then break every
// subsequent Firestore read on refresh. The app keeps its own bounded caches,
// so Firestore itself should stay memory-only.
clearFirestorePersistenceLocalStorage();
// Firebase's modular helpers accept compat objects through their _delegate.
// Keep the compat surface used by existing providers while exposing that bridge.
export const auth = firebase.auth() as firebase.auth.Auth & Auth & {
  useEmulator(url: string, options?: { disableWarnings?: boolean }): void;
};
const firestore = wrapCompatFirestore(firebase.firestore());
const storage = firebase.storage();

if (useFirebaseEmulators) {
  const emulatorHost = process.env.REACT_APP_FIREBASE_EMULATOR_HOST || "127.0.0.1";
  const authPort = Number(process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_PORT) || 9099;
  const firestorePort =
    Number(process.env.REACT_APP_FIRESTORE_EMULATOR_PORT) || 8080;
  auth.useEmulator(`http://${emulatorHost}:${authPort}`, {
    disableWarnings: true
  });
  firestore.useEmulator(emulatorHost, firestorePort);
}
const persistenceReady = Promise.resolve();

export interface FirebaseContextValue {
  firebase: typeof firebase;
  auth: typeof auth;
  firestore: ReturnType<typeof wrapCompatFirestore>;
  storage: firebase.storage.Storage;
  persistenceReady: Promise<void>;
}

const FirebaseContext = createContext<FirebaseContextValue | null>(null);

export const FirebaseProvider = ({ children }: { children?: React.ReactNode }) => {
  return (
    <FirebaseContext.Provider value={{ firebase, auth, firestore, storage, persistenceReady }}>
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = (): FirebaseContextValue => {
  const context = useContext(FirebaseContext);
  if (!context) throw new Error("useFirebase must be used inside FirebaseProvider");
  return context;
};
